/**
 * Page Context Configuration
 * 
 * Maps routes to contextual quick actions for the AI Assistant.
 * Each page context provides 2 relevant actions + "More Options" based on the current page.
 * 
 * Actions are aligned with AI Agent Actions documented in:
 * /docs/AI_AGENT_ACTIONS.md
 */
import {
  BarChart3,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarPlus,
  Clock,
  CloudSun,
  CreditCard,
  DollarSign,
  FileText,
  Flag,
  Hourglass,
  Image,
  LayoutDashboard,
  ListChecks,
  MapPin,
  Megaphone,
  MoreHorizontal,
  PenLine,
  Receipt,
  Search,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Trophy,
  User,
  UserCog,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";
import type { QuickAction } from "./QuickActionCard";

// Special action ID for "Show More Options"
export const SHOW_MORE_ACTION_ID = "show_more_options";

// "Show More Options" action - shown as third action on all pages
export const SHOW_MORE_ACTION: QuickAction = {
  id: SHOW_MORE_ACTION_ID,
  title: "More Options",
  description: "Browse all available actions",
  icon: MoreHorizontal,
  prompt: "", // Empty prompt - handled specially
};

export interface PageContext {
  pageId: string;
  pageName: string;
  actions: QuickAction[];
  /** Category IDs from allActionsConfig that are relevant for this page */
  relevantCategories: string[];
}

// Default actions shown when no specific context matches
const DEFAULT_ACTIONS: QuickAction[] = [
  {
    id: "search_customers",
    title: "Search Customer",
    description: "Find a customer by name or email",
    icon: Search,
    prompt: "search_customers: Find a customer",
  },
  {
    id: "check_availability",
    title: "Check Availability",
    description: "See available time slots",
    icon: Calendar,
    prompt: "check_availability: What times are available today?",
  },
  {
    id: "get_bookings",
    title: "Today's Bookings",
    description: "View all bookings for today",
    icon: CalendarCheck,
    prompt: "get_bookings: Show me today's bookings",
  },
];

// Page-specific action configurations
const PAGE_CONTEXTS: Record<string, PageContext> = {
  // ============================================
  // DASHBOARD
  // ============================================
  dashboard: {
    pageId: "dashboard",
    pageName: "Dashboard",
    relevantCategories: ["analytics", "booking", "customer"],
    actions: [
      {
        id: "get_dashboard_analytics",
        title: "Dashboard Overview",
        description: "KPIs, revenue, and booking trends",
        icon: LayoutDashboard,
        prompt: "get_dashboard_analytics: Show me the dashboard overview with KPIs",
      },
      {
        id: "get_bookings",
        title: "Today's Bookings",
        description: "View all bookings for today",
        icon: CalendarCheck,
        prompt: "get_bookings: Show me today's bookings",
      },
      {
        id: "get_customer_statistics",
        title: "Customer Stats",
        description: "Total customers and members",
        icon: Users,
        prompt: "get_customer_statistics: Show me customer statistics",
      },
    ],
  },

  // ============================================
  // BOOKINGS
  // ============================================
  bookings: {
    pageId: "bookings",
    pageName: "Bookings",
    relevantCategories: ["booking", "waitlist", "bay"],
    actions: [
      {
        id: "create_booking",
        title: "Create Booking",
        description: "Book a bay for a customer",
        icon: CalendarPlus,
        prompt: "create_booking: I want to create a new booking",
      },
      {
        id: "check_availability",
        title: "Check Availability",
        description: "See available time slots",
        icon: Calendar,
        prompt: "check_availability: What times are available today?",
      },
      {
        id: "get_waitlist_entries",
        title: "View Waitlist",
        description: "See customers waiting for slots",
        icon: ListChecks,
        prompt: "get_waitlist_entries: Show me the current waitlist",
      },
    ],
  },

  "booking-calendar": {
    pageId: "booking-calendar",
    pageName: "Booking Calendar",
    relevantCategories: ["booking", "waitlist", "bay"],
    actions: [
      {
        id: "create_booking",
        title: "Create Booking",
        description: "Book a bay for a customer",
        icon: CalendarPlus,
        prompt: "create_booking: I want to create a new booking",
      },
      {
        id: "check_availability",
        title: "Check Availability",
        description: "Find available time slots",
        icon: Clock,
        prompt: "check_availability: What times are available today?",
      },
      {
        id: "get_calendar_bookings",
        title: "Calendar View",
        description: "Monthly booking overview",
        icon: CalendarCheck,
        prompt: "get_calendar_bookings: Show me this month's booking calendar",
      },
    ],
  },

  waitlist: {
    pageId: "waitlist",
    pageName: "Waitlist",
    relevantCategories: ["waitlist", "booking"],
    actions: [
      {
        id: "get_waitlist_entries",
        title: "View Waitlist",
        description: "See all waitlist entries",
        icon: ListChecks,
        prompt: "get_waitlist_entries: Show me the current waitlist",
      },
      {
        id: "add_to_waitlist",
        title: "Add to Waitlist",
        description: "Add a customer to waitlist",
        icon: UserPlus,
        prompt: "add_to_waitlist: I want to add a customer to the waitlist",
      },
      {
        id: "offer_waitlist_slot",
        title: "Offer Slot",
        description: "Offer available slot to waitlist",
        icon: Calendar,
        prompt: "offer_waitlist_slot: Offer a slot to a waitlist customer",
      },
    ],
  },

  // ============================================
  // CUSTOMERS
  // ============================================
  customers: {
    pageId: "customers",
    pageName: "Customers",
    relevantCategories: ["customer", "membership"],
    actions: [
      {
        id: "search_customers",
        title: "Search Customer",
        description: "Find a customer by name or email",
        icon: Search,
        prompt: "search_customers: Search for a customer",
      },
      {
        id: "create_customer",
        title: "Add Customer",
        description: "Create a new customer profile",
        icon: UserPlus,
        prompt: "create_customer: I want to add a new customer",
      },
      {
        id: "get_customer_statistics",
        title: "Customer Stats",
        description: "View customer statistics",
        icon: Users,
        prompt: "get_customer_statistics: Show me customer statistics",
      },
    ],
  },

  "customer-members": {
    pageId: "customer-members",
    pageName: "Member Customers",
    relevantCategories: ["customer", "membership"],
    actions: [
      {
        id: "search_customers",
        title: "Search Member",
        description: "Find a member by name",
        icon: Search,
        prompt: "search_customers: Search for a member customer",
      },
      {
        id: "get_user_memberships",
        title: "View Memberships",
        description: "See all user memberships",
        icon: Users,
        prompt: "get_user_memberships: Show me all active memberships",
      },
      {
        id: "get_membership_statistics",
        title: "Expiring Soon",
        description: "Members with expiring plans",
        icon: Hourglass,
        prompt: "get_membership_statistics: Show me memberships expiring this month",
      },
    ],
  },

  // ============================================
  // MEMBERSHIPS
  // ============================================
  "membership-plans": {
    pageId: "membership-plans",
    pageName: "Membership Plans",
    relevantCategories: ["membership"],
    actions: [
      {
        id: "get_membership_plans",
        title: "View Plans",
        description: "See all membership plans",
        icon: FileText,
        prompt: "get_membership_plans: Show me all membership plans",
      },
      {
        id: "create_membership_plan",
        title: "Create Plan",
        description: "Add a new membership plan",
        icon: CalendarPlus,
        prompt: "create_membership_plan: I want to create a new membership plan",
      },
      {
        id: "get_membership_statistics",
        title: "Plan Statistics",
        description: "Membership plan performance",
        icon: BarChart3,
        prompt: "get_membership_statistics: Give me membership plan statistics",
      },
    ],
  },

  members: {
    pageId: "members",
    pageName: "Members",
    relevantCategories: ["membership", "customer"],
    actions: [
      {
        id: "get_user_memberships",
        title: "View Members",
        description: "See all active members",
        icon: Users,
        prompt: "get_user_memberships: Show me all active members",
      },
      {
        id: "update_customer_membership",
        title: "Assign Membership",
        description: "Assign a plan to a customer",
        icon: UserPlus,
        prompt: "update_customer_membership: I want to assign a membership to a customer",
      },
      {
        id: "get_membership_statistics",
        title: "Member Stats",
        description: "Membership statistics",
        icon: BarChart3,
        prompt: "get_membership_statistics: Show me membership statistics including churn rate",
      },
    ],
  },

  // ============================================
  // TRANSACTIONS
  // ============================================
  transactions: {
    pageId: "transactions",
    pageName: "Transactions",
    relevantCategories: ["transaction"],
    actions: [
      {
        id: "get_transactions",
        title: "Today's Transactions",
        description: "View all transactions today",
        icon: Receipt,
        prompt: "get_transactions: Show me today's transactions",
      },
      {
        id: "process_refund",
        title: "Process Refund",
        description: "Find a transaction to refund",
        icon: DollarSign,
        prompt: "process_refund: I need to process a refund",
      },
      {
        id: "get_transaction_statistics",
        title: "Revenue Summary",
        description: "Today's revenue breakdown",
        icon: Wallet,
        prompt: "get_transaction_statistics: Give me today's revenue summary",
      },
    ],
  },

  // ============================================
  // LESSONS & COACHING
  // ============================================
  lessons: {
    pageId: "lessons",
    pageName: "Lessons & Fittings",
    relevantCategories: ["coaching"],
    actions: [
      {
        id: "get_coaches",
        title: "View Coaches",
        description: "See all available coaches",
        icon: Users,
        prompt: "get_coaches: Show me all coaches",
      },
      {
        id: "check_coach_availability",
        title: "Coach Availability",
        description: "Check coach schedules",
        icon: Clock,
        prompt: "check_coach_availability: Show me coach availability for today",
      },
      {
        id: "get_coach_weekly_hours",
        title: "Weekly Hours",
        description: "Coach weekly availability",
        icon: CalendarClock,
        prompt: "get_coach_weekly_hours: Show me coach weekly hours",
      },
    ],
  },

  coaches: {
    pageId: "coaches",
    pageName: "Coaches",
    relevantCategories: ["coaching"],
    actions: [
      {
        id: "get_coaches",
        title: "All Coaches",
        description: "View all coaches",
        icon: Users,
        prompt: "get_coaches: Show me all coaches",
      },
      {
        id: "check_coach_availability",
        title: "Check Availability",
        description: "Check coach schedules",
        icon: Clock,
        prompt: "check_coach_availability: Check availability for a coach",
      },
      {
        id: "get_coach_details",
        title: "Coach Details",
        description: "Get coach information",
        icon: User,
        prompt: "get_coach_details: Show me details for a coach",
      },
    ],
  },

  // ============================================
  // EVENTS & LEAGUES
  // ============================================
  events: {
    pageId: "events",
    pageName: "Events",
    relevantCategories: ["event"],
    actions: [
      {
        id: "get_events",
        title: "View Events",
        description: "See all scheduled events",
        icon: Calendar,
        prompt: "get_events: Show me all upcoming events",
      },
      {
        id: "create_event",
        title: "Create Event",
        description: "Schedule a new event",
        icon: CalendarPlus,
        prompt: "create_event: I want to create a new event",
      },
      {
        id: "get_event_statistics",
        title: "Event Stats",
        description: "Event participation metrics",
        icon: BarChart3,
        prompt: "get_event_statistics: Show me event statistics",
      },
    ],
  },

  leagues: {
    pageId: "leagues",
    pageName: "Leagues",
    relevantCategories: ["event"],
    actions: [
      {
        id: "get_leagues",
        title: "View Leagues",
        description: "See all leagues",
        icon: Trophy,
        prompt: "get_leagues: Show me all leagues",
      },
      {
        id: "create_league",
        title: "Create League",
        description: "Set up a new league",
        icon: CalendarPlus,
        prompt: "create_league: I want to create a new league",
      },
      {
        id: "get_league_standings",
        title: "Standings",
        description: "View league standings",
        icon: BarChart3,
        prompt: "get_league_standings: Show me league standings",
      },
    ],
  },

  // ============================================
  // ANALYTICS
  // ============================================
  analytics: {
    pageId: "analytics",
    pageName: "Analytics",
    relevantCategories: ["analytics"],
    actions: [
      {
        id: "get_dashboard_analytics",
        title: "Dashboard Analytics",
        description: "KPIs and trends",
        icon: LayoutDashboard,
        prompt: "get_dashboard_analytics: Show me dashboard analytics",
      },
      {
        id: "get_revenue_trends",
        title: "Revenue Trends",
        description: "Revenue over time",
        icon: DollarSign,
        prompt: "get_revenue_trends: Show me revenue trends",
      },
      {
        id: "get_bay_utilization",
        title: "Bay Utilization",
        description: "Simulator usage metrics",
        icon: BarChart3,
        prompt: "get_bay_utilization: Show me bay utilization metrics",
      },
    ],
  },

  // ============================================
  // BAYS / SIMULATORS
  // ============================================
  bays: {
    pageId: "bays",
    pageName: "Bays",
    relevantCategories: ["bay", "booking"],
    actions: [
      {
        id: "get_bays",
        title: "View Bays",
        description: "See all bays and status",
        icon: MapPin,
        prompt: "get_bays: Show me all bays with their status",
      },
      {
        id: "block_bay_hours",
        title: "Block Hours",
        description: "Block bay for maintenance",
        icon: Clock,
        prompt: "block_bay_hours: I need to block hours for a bay",
      },
      {
        id: "get_bay_utilization",
        title: "Utilization",
        description: "Bay usage metrics",
        icon: BarChart3,
        prompt: "get_bay_utilization: Show me bay utilization",
      },
    ],
  },

  // ============================================
  // SETTINGS - PRICING
  // ============================================
  pricing: {
    pageId: "pricing",
    pageName: "Pricing",
    relevantCategories: ["pricing"],
    actions: [
      {
        id: "get_seasons",
        title: "View Seasons",
        description: "See all pricing seasons",
        icon: Calendar,
        prompt: "get_seasons: Show me all pricing seasons",
      },
      {
        id: "get_pricing_format",
        title: "Pricing Format",
        description: "Current pricing configuration",
        icon: DollarSign,
        prompt: "get_pricing_format: Show me the current pricing format",
      },
      {
        id: "get_operation_hours",
        title: "Operation Hours",
        description: "Hours with pricing",
        icon: Clock,
        prompt: "get_operation_hours: Show me operation hours with pricing",
      },
    ],
  },

  // ============================================
  // SETTINGS - BUSINESS HOURS
  // ============================================
  "business-hours": {
    pageId: "business-hours",
    pageName: "Business Hours",
    relevantCategories: ["pricing", "settings"],
    actions: [
      {
        id: "get_business_hours",
        title: "View Hours",
        description: "Current business hours",
        icon: Clock,
        prompt: "get_business_hours: Show me business hours",
      },
      {
        id: "update_business_hours",
        title: "Update Hours",
        description: "Change business hours",
        icon: PenLine,
        prompt: "update_business_hours: I want to update business hours",
      },
      {
        id: "get_business_holidays",
        title: "Holidays",
        description: "View business holidays",
        icon: Calendar,
        prompt: "get_business_holidays: Show me business holidays",
      },
    ],
  },

  // ============================================
  // SETTINGS - VENUE
  // ============================================
  venue: {
    pageId: "venue",
    pageName: "Venue Settings",
    relevantCategories: ["settings", "notification"],
    actions: [
      {
        id: "get_venue_settings",
        title: "Venue Settings",
        description: "View venue configuration",
        icon: Settings,
        prompt: "get_venue_settings: Show me venue settings",
      },
      {
        id: "get_branding_settings",
        title: "Branding",
        description: "View branding settings",
        icon: Image,
        prompt: "get_branding_settings: Show me branding settings",
      },
      {
        id: "get_notification_settings",
        title: "Notifications",
        description: "View notification settings",
        icon: Megaphone,
        prompt: "get_notification_settings: Show me notification settings",
      },
    ],
  },

  // ============================================
  // WEBSITE CMS
  // ============================================
  "website-cms": {
    pageId: "website-cms",
    pageName: "Website CMS",
    relevantCategories: ["cms"],
    actions: [
      {
        id: "get_cms_hero_content",
        title: "Hero Section",
        description: "Edit hero content",
        icon: Image,
        prompt: "get_cms_hero_content: Show me the hero section content",
      },
      {
        id: "get_about_us_content",
        title: "About Us",
        description: "View about page content",
        icon: FileText,
        prompt: "get_about_us_content: Show me the about us content",
      },
      {
        id: "get_faq_content",
        title: "FAQ",
        description: "View FAQ content",
        icon: FileText,
        prompt: "get_faq_content: Show me the FAQ content",
      },
    ],
  },

  // ============================================
  // MARKETING POPUPS
  // ============================================
  popups: {
    pageId: "popups",
    pageName: "Marketing Popups",
    relevantCategories: ["popup"],
    actions: [
      {
        id: "get_popup_list",
        title: "View Popups",
        description: "See all marketing popups",
        icon: Megaphone,
        prompt: "get_popup_list: Show me all marketing popups",
      },
      {
        id: "create_popup",
        title: "Create Popup",
        description: "Create a new popup",
        icon: CalendarPlus,
        prompt: "create_popup: I want to create a new marketing popup",
      },
      {
        id: "get_popup_analytics",
        title: "Popup Analytics",
        description: "View popup performance",
        icon: BarChart3,
        prompt: "get_popup_analytics: Show me popup analytics",
      },
    ],
  },

  // ============================================
  // ADMIN USERS & STAFF
  // ============================================
  "admin-users": {
    pageId: "admin-users",
    pageName: "Admin Users",
    relevantCategories: ["admin", "staff"],
    actions: [
      {
        id: "get_admin_users",
        title: "View Admins",
        description: "See all admin users",
        icon: Users,
        prompt: "get_admin_users: Show me all admin users",
      },
      {
        id: "create_admin_user",
        title: "Add Admin",
        description: "Create a new admin user",
        icon: UserPlus,
        prompt: "create_admin_user: I want to add a new admin user",
      },
      {
        id: "invite_user",
        title: "Invite User",
        description: "Send admin invitation",
        icon: UserCog,
        prompt: "invite_user: I want to invite a new admin",
      },
    ],
  },

  staff: {
    pageId: "staff",
    pageName: "Staff Management",
    relevantCategories: ["staff", "admin"],
    actions: [
      {
        id: "get_staff_list",
        title: "View Staff",
        description: "See all staff members",
        icon: Users,
        prompt: "get_staff_list: Show me all staff members",
      },
      {
        id: "create_staff_pin",
        title: "Create PIN",
        description: "Add staff PIN access",
        icon: ShieldCheck,
        prompt: "create_staff_pin: I want to create a staff PIN",
      },
      {
        id: "get_active_staff",
        title: "Active Staff",
        description: "Currently active staff",
        icon: UserCog,
        prompt: "get_active_staff: Show me currently active staff",
      },
    ],
  },

  // ============================================
  // POS
  // ============================================
  pos: {
    pageId: "pos",
    pageName: "Point of Sale",
    relevantCategories: ["product", "transaction", "customer"],
    actions: [
      {
        id: "get_products",
        title: "View Products",
        description: "See all products",
        icon: FileText,
        prompt: "get_products: Show me all products",
      },
      {
        id: "search_customers",
        title: "Find Customer",
        description: "Look up a customer",
        icon: Search,
        prompt: "search_customers: Search for a customer",
      },
      {
        id: "get_transactions",
        title: "Recent Sales",
        description: "View recent transactions",
        icon: CreditCard,
        prompt: "get_transactions: Show me recent transactions",
      },
    ],
  },

  // ============================================
  // PRODUCTS
  // ============================================

  // ============================================
  // GOLF — TEE TIMES
  // ============================================
  "golf-tee-times": {
    pageId: "golf-tee-times",
    pageName: "Tee Sheets",
    relevantCategories: ["booking", "bay", "coaching"],
    actions: [
      {
        id: "check_availability",
        title: "Check Availability",
        description: "See open tee times",
        icon: Clock,
        prompt: "check_availability: What tee times are available today?",
      },
      {
        id: "get_bookings",
        title: "Today's Tee Sheet",
        description: "View today's tee time bookings",
        icon: CalendarCheck,
        prompt: "get_bookings: Show me today's tee sheet",
      },
      {
        id: "create_booking",
        title: "Book Tee Time",
        description: "Create a new tee time booking",
        icon: CalendarPlus,
        prompt: "create_booking: I want to book a tee time",
      },
    ],
  },

  // ============================================
  // GOLF — CADDIES
  // ============================================
  "golf-caddies": {
    pageId: "golf-caddies",
    pageName: "Caddies",
    relevantCategories: ["coaching", "booking"],
    actions: [
      {
        id: "get_coaches",
        title: "View Caddies",
        description: "See all caddies and their status",
        icon: Users,
        prompt: "get_coaches: Show me all caddies with their availability",
      },
      {
        id: "check_coach_availability",
        title: "Caddie Availability",
        description: "Check who's available today",
        icon: Clock,
        prompt: "check_coach_availability: Which caddies are available today?",
      },
      {
        id: "get_coach_weekly_hours",
        title: "Caddie Schedule",
        description: "View weekly caddie schedules",
        icon: CalendarClock,
        prompt: "get_coach_weekly_hours: Show me caddie schedules for this week",
      },
    ],
  },

  // ============================================
  // GOLF — COURSES
  // ============================================
  "golf-courses": {
    pageId: "golf-courses",
    pageName: "Courses",
    relevantCategories: ["bay", "booking"],
    actions: [
      {
        id: "get_bays",
        title: "View Courses",
        description: "See all courses and holes",
        icon: Flag,
        prompt: "get_bays: Show me all courses with their status",
      },
      {
        id: "get_bay_utilization",
        title: "Course Utilization",
        description: "Usage metrics per course",
        icon: BarChart3,
        prompt: "get_bay_utilization: Show me course utilization metrics",
      },
      {
        id: "check_availability",
        title: "Availability",
        description: "Check course availability",
        icon: Clock,
        prompt: "check_availability: What courses have availability today?",
      },
    ],
  },

  // ============================================
  // GOLF — CARTS
  // ============================================
  "golf-carts": {
    pageId: "golf-carts",
    pageName: "Golf Carts",
    relevantCategories: ["bay", "booking"],
    actions: [
      {
        id: "get_bays",
        title: "Cart Inventory",
        description: "View all golf carts",
        icon: ShoppingCart,
        prompt: "get_bays: Show me all golf carts and their status",
      },
      {
        id: "get_bookings",
        title: "Cart Assignments",
        description: "Today's cart assignments",
        icon: CalendarCheck,
        prompt: "get_bookings: Show me today's cart assignments",
      },
      {
        id: "get_bay_utilization",
        title: "Cart Usage",
        description: "Cart utilization metrics",
        icon: BarChart3,
        prompt: "get_bay_utilization: Show me golf cart utilization",
      },
    ],
  },

  // ============================================
  // GOLF — WEATHER
  // ============================================
  "golf-weather": {
    pageId: "golf-weather",
    pageName: "Weather",
    relevantCategories: ["booking", "analytics"],
    actions: [
      {
        id: "check_availability",
        title: "Today's Tee Times",
        description: "Check available times given weather",
        icon: Clock,
        prompt: "check_availability: What tee times are available today given the weather?",
      },
      {
        id: "get_bookings",
        title: "Today's Bookings",
        description: "Bookings that may be weather-affected",
        icon: CloudSun,
        prompt: "get_bookings: Show me today's bookings — are any weather-affected?",
      },
      {
        id: "get_booking_daily_trends",
        title: "Weather Impact",
        description: "How weather affects bookings",
        icon: BarChart3,
        prompt: "get_booking_daily_trends: Show me booking trends this week — how is the weather affecting us?",
      },
    ],
  },

  // ============================================
  // GOLF — DASHBOARD
  // ============================================
  "golf-dashboard": {
    pageId: "golf-dashboard",
    pageName: "Golf Dashboard",
    relevantCategories: ["analytics", "booking", "coaching"],
    actions: [
      {
        id: "get_dashboard_analytics",
        title: "Golf Overview",
        description: "KPIs and round metrics",
        icon: LayoutDashboard,
        prompt: "get_dashboard_analytics: Show me today's golf dashboard overview",
      },
      {
        id: "get_bookings",
        title: "Today's Rounds",
        description: "All tee times today",
        icon: CalendarCheck,
        prompt: "get_bookings: Show me today's rounds and tee times",
      },
      {
        id: "get_bay_utilization",
        title: "Course Utilization",
        description: "Course/tee usage metrics",
        icon: BarChart3,
        prompt: "get_bay_utilization: How are our courses being utilized?",
      },
    ],
  },

  // ============================================
  // GOLF — LEAGUES
  // ============================================
  "golf-leagues": {
    pageId: "golf-leagues",
    pageName: "Golf Leagues",
    relevantCategories: ["event", "booking"],
    actions: [
      {
        id: "get_events",
        title: "View Leagues",
        description: "See all golf leagues",
        icon: Trophy,
        prompt: "get_events: Show me all golf leagues",
      },
      {
        id: "create_event",
        title: "Create League",
        description: "Set up a new golf league",
        icon: CalendarPlus,
        prompt: "create_event: I want to create a new golf league",
      },
      {
        id: "get_event_statistics",
        title: "League Stats",
        description: "League participation metrics",
        icon: BarChart3,
        prompt: "get_event_statistics: Show me league statistics",
      },
    ],
  },

  // ============================================
  // GOLF — WAITLIST
  // ============================================
  "golf-waitlist": {
    pageId: "golf-waitlist",
    pageName: "Golf Waitlist",
    relevantCategories: ["waitlist", "booking"],
    actions: [
      {
        id: "get_waitlist_entries",
        title: "View Waitlist",
        description: "Golfers waiting for tee times",
        icon: ListChecks,
        prompt: "get_waitlist_entries: Show me golfers on the tee time waitlist",
      },
      {
        id: "offer_waitlist_slot",
        title: "Offer Tee Time",
        description: "Offer an open tee time to waitlist",
        icon: Calendar,
        prompt: "offer_waitlist_slot: Offer a tee time to someone on the waitlist",
      },
      {
        id: "check_availability",
        title: "Open Tee Times",
        description: "Check available tee times to offer",
        icon: Clock,
        prompt: "check_availability: What tee times are open that I can offer?",
      },
    ],
  },

  // ============================================
  // GOLF — CANCELLATIONS
  // ============================================
  "golf-cancellations": {
    pageId: "golf-cancellations",
    pageName: "Golf Cancellations",
    relevantCategories: ["booking", "waitlist"],
    actions: [
      {
        id: "get_bookings",
        title: "Cancelled Tee Times",
        description: "Recent cancellations",
        icon: CalendarCheck,
        prompt: "get_bookings: Show me recent tee time cancellations",
      },
      {
        id: "get_waitlist_entries",
        title: "Waitlist",
        description: "Offer cancelled slots to waitlist",
        icon: ListChecks,
        prompt: "get_waitlist_entries: Show me the waitlist — can we fill any cancelled slots?",
      },
      {
        id: "get_booking_statistics",
        title: "Cancellation Rate",
        description: "Cancellation trends",
        icon: BarChart3,
        prompt: "get_booking_statistics: What's our tee time cancellation rate?",
      },
    ],
  },

  // ============================================
  // GOLF — OPERATION HOURS
  // ============================================
  "golf-operation-hours": {
    pageId: "golf-operation-hours",
    pageName: "Golf Operation Hours",
    relevantCategories: ["pricing", "settings"],
    actions: [
      {
        id: "get_operation_hours",
        title: "Operation Hours",
        description: "Current golf operation hours",
        icon: Clock,
        prompt: "get_operation_hours: Show me golf operation hours",
      },
      {
        id: "get_business_hours",
        title: "Business Hours",
        description: "View business hours",
        icon: Clock,
        prompt: "get_business_hours: Show me business hours",
      },
      {
        id: "get_business_holidays",
        title: "Course Closures",
        description: "View holidays and closures",
        icon: Calendar,
        prompt: "get_business_holidays: Show me upcoming course closures and holidays",
      },
    ],
  },

  // ============================================
  // GOLF — SETTINGS
  // ============================================
  "golf-settings": {
    pageId: "golf-settings",
    pageName: "Golf Settings",
    relevantCategories: ["settings", "pricing"],
    actions: [
      {
        id: "get_customer_booking_settings",
        title: "Booking Policy",
        description: "Tee time booking rules",
        icon: Settings,
        prompt: "get_customer_booking_settings: Show me tee time booking policies",
      },
      {
        id: "get_seasons",
        title: "Pricing Seasons",
        description: "Seasonal tee time pricing",
        icon: DollarSign,
        prompt: "get_seasons: Show me pricing seasons for tee times",
      },
      {
        id: "get_venue_settings",
        title: "Venue Settings",
        description: "Golf venue configuration",
        icon: Settings,
        prompt: "get_venue_settings: Show me venue settings",
      },
    ],
  },

  products: {
    pageId: "products",
    pageName: "Products",
    relevantCategories: ["product"],
    actions: [
      {
        id: "get_products",
        title: "View Products",
        description: "See all products",
        icon: FileText,
        prompt: "get_products: Show me all products",
      },
      {
        id: "create_product",
        title: "Add Product",
        description: "Create a new product",
        icon: CalendarPlus,
        prompt: "create_product: I want to add a new product",
      },
      {
        id: "get_low_stock_products",
        title: "Low Stock",
        description: "Products running low",
        icon: BarChart3,
        prompt: "get_low_stock_products: Show me products with low stock",
      },
    ],
  },

  // ============================================
  // MEDIA LIBRARY
  // ============================================
  media: {
    pageId: "media",
    pageName: "Media Library",
    relevantCategories: ["media"],
    actions: [
      {
        id: "get_media_files",
        title: "View Media",
        description: "Browse media files",
        icon: Image,
        prompt: "get_media_files: Show me media files",
      },
      {
        id: "get_media_storage_stats",
        title: "Storage Stats",
        description: "Storage usage statistics",
        icon: BarChart3,
        prompt: "get_media_storage_stats: Show me storage usage statistics",
      },
    ],
  },

  // ============================================
  // CONTACT FORMS / SUBSCRIPTIONS
  // ============================================
  contacts: {
    pageId: "contacts",
    pageName: "Contact Forms",
    relevantCategories: ["contact"],
    actions: [
      {
        id: "get_contact_forms",
        title: "View Contacts",
        description: "See contact submissions",
        icon: FileText,
        prompt: "get_contact_forms: Show me contact form submissions",
      },
      {
        id: "get_newsletter_subscribers",
        title: "Newsletter",
        description: "View subscribers",
        icon: Users,
        prompt: "get_newsletter_subscribers: Show me newsletter subscribers",
      },
      {
        id: "get_subscription_statistics",
        title: "Stats",
        description: "Subscription statistics",
        icon: BarChart3,
        prompt: "get_subscription_statistics: Show me subscription statistics",
      },
    ],
  },

  // ============================================
  // REPORTS
  // ============================================
  reports: {
    pageId: "reports",
    pageName: "Reports",
    relevantCategories: ["analytics"],
    actions: [
      {
        id: "get_scheduled_reports",
        title: "Scheduled Reports",
        description: "View scheduled reports",
        icon: Calendar,
        prompt: "get_scheduled_reports: Show me scheduled reports",
      },
      {
        id: "create_scheduled_report",
        title: "Create Report",
        description: "Schedule a new report",
        icon: CalendarPlus,
        prompt: "create_scheduled_report: I want to create a scheduled report",
      },
      {
        id: "export_dashboard_data",
        title: "Export Data",
        description: "Export dashboard data",
        icon: FileText,
        prompt: "export_dashboard_data: I want to export dashboard data",
      },
    ],
  },
};

/**
 * Get the page context ID from a pathname
 */
export const getPageContextId = (pathname: string): string => {
  // Remove leading slash and get segments
  const segments = pathname.replace(/^\//, "").split("/");
  const primary = segments[0] || "dashboard";
  const secondary = segments[1];

  // Check for specific sub-routes first
  if (primary === "booking" && secondary === "calendar") return "booking-calendar";
  if (primary === "bookings" && secondary === "waitlist") return "waitlist";
  if (primary === "bookings" && secondary === "monthly") return "booking-calendar";
  if (primary === "customers" && secondary === "members") return "customer-members";
  if (primary === "membership" && secondary === "plans") return "membership-plans";
  if (primary === "membership" && secondary === "members") return "members";
  if (primary === "lessons") return "lessons";
  if (primary === "lessons" && secondary === "coaches") return "coaches";
  if (primary === "events") return "events";
  if (primary === "league-events") return "leagues";
  if (primary === "settings" && secondary === "pricing") return "pricing";
  if (primary === "settings" && secondary === "business-hours") return "business-hours";
  if (primary === "settings" && secondary === "venue") return "venue";
  if (primary === "settings" && secondary === "users") return "admin-users";
  if (primary === "settings" && secondary === "staff") return "staff";
  if (primary === "website-cms") return "website-cms";
  if (primary === "marketing-popups") return "popups";
  if (primary === "media-library") return "media";
  if (primary === "contact-forms") return "contacts";
  if (primary === "reports") return "reports";
  if (primary === "products") return "products";
  if (primary === "pos") return "pos";
  if (primary === "bays") return "bays";

  // Golf routes
  if (primary === "golf") {
    if (secondary === "tee-times") return "golf-tee-times";
    if (secondary === "caddies") return "golf-caddies";
    if (secondary === "courses") return "golf-courses";
    if (secondary === "carts") return "golf-carts";
    if (secondary === "weather") return "golf-weather";
    if (secondary === "dashboard") return "golf-dashboard";
    if (secondary === "leagues") return "golf-leagues";
    if (secondary === "waitlist") return "golf-waitlist";
    if (secondary === "cancellations") return "golf-cancellations";
    if (secondary === "operation-hours") return "golf-operation-hours";
    if (secondary === "settings") return "golf-settings";
    return "golf-dashboard";
  }

  // Return primary segment or default
  return PAGE_CONTEXTS[primary] ? primary : "dashboard";
};

/**
 * Get quick actions for a given pathname
 * Returns 2 context-specific actions + "More Options" as the third
 */
export const getQuickActionsForPath = (pathname: string): QuickAction[] => {
  const contextId = getPageContextId(pathname);
  const context = PAGE_CONTEXTS[contextId];
  const contextActions = context?.actions || DEFAULT_ACTIONS;
  
  // Take first 2 context-specific actions and add "More Options"
  return [...contextActions.slice(0, 2), SHOW_MORE_ACTION];
};

// Default relevant categories for generic context
const DEFAULT_RELEVANT_CATEGORIES = ["customer", "booking", "analytics"];

/**
 * Get page context info for a given pathname
 */
export const getPageContext = (pathname: string): PageContext => {
  const contextId = getPageContextId(pathname);
  return (
    PAGE_CONTEXTS[contextId] || {
      pageId: "default",
      pageName: "Dashboard",
      actions: DEFAULT_ACTIONS,
      relevantCategories: DEFAULT_RELEVANT_CATEGORIES,
    }
  );
};

export { DEFAULT_ACTIONS, PAGE_CONTEXTS };
